import { useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { useActivities } from '../hooks/useActivities'
import { useComponents } from '../hooks/useComponents'
import { useServices } from '../hooks/useServices'
import { useRoster } from '../hooks/useRoster'
import { useEventRisks } from '../hooks/useRisks'
import { useNotes } from '../hooks/useNotes'
import { useEventBudget } from '../hooks/useBudget'
import { useMe } from '../hooks/useMe'
import { formatEventDates, parseIsoDate } from '../lib/dates'
import { scoreBand } from '../components/RiskEditor'

// EventPlanPrint - dedicated print-only route that the browser's print
// dialog renders as a PDF. Use this rather than a JS PDF library so we
// don't pull in @react-pdf/renderer (avoids npm install in this autonomous
// session). The page renders without the app shell; the user prints or saves
// as PDF from the browser's own dialog (File ▸ Print / Ctrl+P) — we no longer
// auto-open the print dialog.
//
// Section order matches DOWT_Event_Plan_-_May_2026.docx:
//   Cover · Overview · Activities · Services · Roster · Risks · Components ·
//   Decisions · Budget (financial users only) · Sign-off.

export function EventPlanPrint() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const { data: me } = useMe()
  const { data: event } = useEvent(id)
  const { data: activities } = useActivities(id)
  const { data: components } = useComponents(id)
  const { data: services } = useServices(id)
  const { data: roster } = useRoster(id)
  const { data: risks } = useEventRisks(id)
  const { data: notes } = useNotes(id)
  const { data: budget } = useEventBudget(id)

  if (!event) {
    return <PrintShell><p>Loading…</p></PrintShell>
  }

  const pinnedNotes = (notes ?? []).filter((n) => n.isPinned)

  return (
    <PrintShell>
      <CoverPage event={event} />
      <Section title="1. Overview">
        <p><strong>Status:</strong> {event.status}</p>
        <p><strong>Type:</strong> {event.type.name}</p>
        <p><strong>Dates:</strong> {formatEventDates(event.dates, event.startDate, event.endDate)}</p>
        {event.location && <p><strong>Location:</strong> {event.location}</p>}
        {event.capacity != null && <p><strong>Capacity:</strong> {event.capacity.toLocaleString()}</p>}
        {event.manager && <p><strong>Event manager:</strong> {event.manager.name}</p>}
        {event.description && <p style={{ marginTop: 8 }}>{event.description}</p>}
      </Section>

      <Section title="2. Programme &amp; activities">
        {(activities ?? []).length === 0 ? (
          <Empty>No activities recorded.</Empty>
        ) : (
          <Table headers={['Name', 'Time', 'Location', 'Staff', 'Supplier']}>
            {(activities ?? []).map((a) => (
              <tr key={a.id}>
                <td>
                  <strong>{a.name}</strong>
                  {a.description && <div style={{ fontSize: 10.5, color: '#5a5a5a', marginTop: 2 }}>{a.description}</div>}
                </td>
                <td>{a.timeText ?? '—'}</td>
                <td>{a.location ?? '—'}</td>
                <td>{a.staffCount ?? '—'}</td>
                <td>{a.supplier ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="3. Services / timetable">
        {(services ?? []).length === 0 ? (
          <Empty>No services recorded.</Empty>
        ) : (
          <Table headers={['Time', 'Service', 'Loco', 'From → To', 'Capacity', 'Crew']}>
            {(services ?? []).map((s) => (
              <tr key={s.id}>
                <td style={{ fontFamily: 'monospace' }}>{s.timeText}</td>
                <td><strong>{s.serviceLabel}</strong></td>
                <td>{s.loco ?? '—'}</td>
                <td>{s.originStation && s.destinationStation ? `${s.originStation} → ${s.destinationStation}` : '—'}</td>
                <td>{s.capacity ?? '—'}</td>
                <td style={{ fontSize: 10 }}>
                  {(s.crew ?? []).map((c) => `${c.avatarInitials} (${c.roleName})`).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="4. Roster">
        {roster && roster.assignments.length > 0 ? (
          <Table headers={['Role', 'Date', 'Person']}>
            {roster.assignments
              .filter((a) => a.userId != null)
              .map((a) => {
                const role = roster.roles.find((r) => r.id === a.roleId)
                return (
                  <tr key={a.id}>
                    <td>{role?.name ?? '—'}</td>
                    <td style={{ fontFamily: 'monospace' }}>{a.assignmentDate}</td>
                    <td>{a.userName ?? '—'}</td>
                  </tr>
                )
              })}
          </Table>
        ) : (
          <Empty>No assignments yet.</Empty>
        )}
      </Section>

      <Section title="5. Risks">
        {(risks ?? []).length === 0 ? (
          <Empty>No risks recorded.</Empty>
        ) : (
          <Table headers={['Hazard', 'Inherent', 'Controls', 'Residual', 'Owner', 'Signed off']}>
            {(risks ?? []).map((r) => {
              const highlight = r.residualScore >= 10
              return (
                <tr
                  key={r.id}
                  style={highlight ? { borderLeft: '3px solid #7a2329' } : undefined}
                >
                  <td>
                    <strong>{r.hazard}</strong>
                    <div style={{ fontSize: 10, color: '#5a5a5a', textTransform: 'uppercase' }}>{r.category}</div>
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: 'monospace' }}>{r.inherentScore}</td>
                  <td style={{ fontSize: 10.5 }}>{r.controls}</td>
                  <td style={{
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    background: scoreBand(r.residualScore).bg,
                    color: scoreBand(r.residualScore).fg
                  }}>
                    {r.residualScore}
                  </td>
                  <td>{r.owner?.name ?? '—'}</td>
                  <td>{r.signedOff ? `✓ ${r.signedOff.by.name ?? ''}` : '—'}</td>
                </tr>
              )
            })}
          </Table>
        )}
      </Section>

      <Section title="6. Production components">
        {(components ?? []).length === 0 ? (
          <Empty>No components recorded.</Empty>
        ) : (
          <Table headers={['Category', 'Name', 'Qty', 'Status', me?.hasFinancial ? 'Cost' : '']}>
            {(components ?? []).map((c) => (
              <tr key={c.id}>
                <td style={{ fontSize: 10, color: '#5a5a5a' }}>{c.category}</td>
                <td>
                  <strong>{c.name}</strong>
                  {c.description && <div style={{ fontSize: 10.5, color: '#5a5a5a' }}>{c.description}</div>}
                </td>
                <td style={{ textAlign: 'right' }}>{c.quantity ?? '—'}</td>
                <td>{c.status}</td>
                {me?.hasFinancial && <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {c.costEstimate != null ? `£${c.costEstimate.toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                </td>}
                {!me?.hasFinancial && <td />}
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="7. Decisions log">
        {pinnedNotes.length === 0 ? (
          <Empty>No decisions recorded.</Empty>
        ) : (
          <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
            {pinnedNotes.map((n) => (
              <li key={n.id} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: '3px solid #9b7f2a' }}>
                {n.pinnedLabel && <div style={{ fontWeight: 700, marginBottom: 2 }}>{n.pinnedLabel}</div>}
                <div style={{ whiteSpace: 'pre-wrap' }}>{n.body}</div>
                <div style={{ fontSize: 10, color: '#5a5a5a', marginTop: 2 }}>
                  — {n.author.name ?? 'Someone'}, {new Date(n.createdAt).toLocaleDateString('en-GB')}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {me?.hasFinancial && budget && (
        <Section title="8. Budget summary">
          <p>
            <strong>Cost</strong>: planned £{budget.totals.cost.planned.toLocaleString('en-GB')} ·
            committed £{budget.totals.cost.committed.toLocaleString('en-GB')} ·
            actual £{budget.totals.cost.actual.toLocaleString('en-GB')}
          </p>
          <p>
            <strong>Revenue</strong>: planned £{budget.totals.revenue.planned.toLocaleString('en-GB')} ·
            actual £{budget.totals.revenue.actual.toLocaleString('en-GB')}
          </p>
          <p><strong>Net</strong>: £{budget.totals.net.toLocaleString('en-GB')}</p>
          {budget.baselined && (
            <p style={{ color: '#5a5a5a', fontSize: 10.5 }}>
              Baselined {budget.baselinedAt ? new Date(budget.baselinedAt).toLocaleDateString('en-GB') : ''}
              {budget.baselinedByName ? ` by ${budget.baselinedByName}` : ''}.
            </p>
          )}
        </Section>
      )}

      <Section title={`${me?.hasFinancial && budget ? '9' : '8'}. Sign-off`}>
        <table style={{ width: '100%', marginTop: 20, borderCollapse: 'collapse' }}>
          <tbody>
            <SignRow label="Event manager" />
            <SignRow label="Operations" />
            <SignRow label="Health &amp; Safety" />
            {me?.hasFinancial && <SignRow label="Finance" />}
          </tbody>
        </table>
      </Section>
    </PrintShell>
  )
}

function PrintShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: #fff; }
        }
        .nymr-print-page {
          font-family: 'Source Sans 3', Arial, sans-serif;
          color: #1a1a1a;
          font-size: 12.5px;
          line-height: 1.5;
          max-width: 720px;
          margin: 0 auto;
          padding: 24px;
        }
        .nymr-print-page h1 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 26px;
          color: #0a4a3a;
          margin-bottom: 4px;
        }
        .nymr-print-page h2 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px;
          color: #1a1a1a;
          margin-top: 20px;
          margin-bottom: 8px;
          padding-bottom: 4px;
          border-bottom: 2px solid #9b7f2a;
        }
        .nymr-print-page p { margin-bottom: 6px; }
        .nymr-print-page table {
          width: 100%;
          border-collapse: collapse;
          margin: 8px 0;
          font-size: 11px;
        }
        .nymr-print-page th {
          background: #0a4a3a;
          color: #fff;
          padding: 6px 8px;
          text-align: left;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .nymr-print-page td {
          padding: 5px 8px;
          border-bottom: 1px solid #e5e0d4;
          vertical-align: top;
        }
        .nymr-print-page section { page-break-inside: avoid; }
      `}</style>
      <div className="nymr-print-page">{children}</div>
    </>
  )
}

function CoverPage({ event }: { event: NonNullable<ReturnType<typeof useEvent>['data']> }) {
  return (
    <section style={{ paddingBottom: 30, pageBreakAfter: 'always' }}>
      <div style={{ fontSize: 12, color: '#5a5a5a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
        NYMR · Production Plan
      </div>
      <h1 style={{ fontSize: 36 }}>{event.name}</h1>
      <p style={{ fontSize: 14, color: '#5a5a5a' }}>
        {event.type.name} · {formatEventDates(event.dates, event.startDate, event.endDate)}
      </p>
      {event.location && <p style={{ fontSize: 14 }}>{event.location}</p>}
      <p style={{ fontSize: 11, color: '#5a5a5a', marginTop: 40 }}>
        Generated {new Date().toLocaleString('en-GB')}.
      </p>
    </section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 dangerouslySetInnerHTML={{ __html: title }} />
      {children}
    </section>
  )
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <table>
      <thead>
        <tr>
          {headers.map((h, i) => <th key={i}>{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p style={{ fontStyle: 'italic', color: '#5a5a5a' }}>{children}</p>
}

function SignRow({ label }: { label: string }) {
  return (
    <tr>
      <td style={{ width: 160, padding: '14px 0', borderBottom: '1px solid #d8d2c4' }}>
        <strong dangerouslySetInnerHTML={{ __html: label }} />
      </td>
      <td style={{ padding: '14px 0', borderBottom: '1px solid #d8d2c4' }}>
        <span style={{ color: '#5a5a5a', fontSize: 10 }}>Signature:</span>
      </td>
      <td style={{ padding: '14px 0', borderBottom: '1px solid #d8d2c4', width: 100 }}>
        <span style={{ color: '#5a5a5a', fontSize: 10 }}>Date:</span>
      </td>
    </tr>
  )
}

// Re-use parseIsoDate to avoid unused-import in some bundlers.
void parseIsoDate
